
export interface AppSettings {
  settings_type: string;
  app_color: string | null;
  is_magento_active: number;
  is_delivery_active: number;
  is_vendor_active: number;
  app_favicon_icon: string;
  app_logo_sm_icon: string;
  app_logo: string;
  app_name: string;
  app_logo_background: number;
  from_name: string | null;
  from_address_line_1: string | null;
  from_address_line_2: string | null;
  from_contact_1: string | null;
  primary_color: string;
  app_icon_color: string;
  widget_hover_color: string;
  site_loyalty_page_url: string | null;
  site_url: string | null;
}

export interface SettingsResponse {
  message: string;
  data: AppSettings;
}

class SettingsService {
  private settings: AppSettings | null = null;
  private subscribers: Array<(settings: AppSettings | null) => void> = [];
  private isInitializing: boolean = false;

  constructor() {
    this.settings = null;
    this.subscribers = [];
  }


  async initializeSettings(): Promise<AppSettings> {
    if (this.isInitializing && this.settings) {
      return this.settings;
    }

    this.isInitializing = true;

    try {
       const apiUrl = process.env.REACT_APP_API_URL;
       const params = new URLSearchParams(window.location.search);
      const shop_domain = params.get("shop_domain");
      const local_shop_domain = localStorage.getItem('shop_domain');
     const response = await fetch(`${apiUrl}${local_shop_domain ?? shop_domain}/api/v1/application-settings`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: SettingsResponse = await response.json();
      
      if (data.message === "Settings retrieved successfully") {
        this.settings = data.data;
        this.notifySubscribers();
        return this.settings;
      }
      throw new Error('Failed to fetch settings');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    
      this.settings = this.getFallbackSettings();
      this.notifySubscribers();
      return this.settings;
    } finally {
      this.isInitializing = false;
    }
  }


  private getFallbackSettings(): AppSettings {
    return {
      settings_type: 'main_settings',
      app_color: null,
      is_magento_active: 1,
      is_delivery_active: 0,
      is_vendor_active: 0,
      app_favicon_icon: '',
      app_logo_sm_icon: '',
      app_logo: '',
      app_name: process.env.REACT_APP_STORE_NAME || 'Store',
      app_logo_background: 1,
      from_name: null,
      from_address_line_1: null,
      from_address_line_2: null,
      from_contact_1: null,
      primary_color: process.env.REACT_APP_PRIMARY_COLOR || '#1E3A8A',
      app_icon_color: '#c837ae',
      widget_hover_color: process.env.REACT_APP_PRIMARY_COLOR_HOVER || '#2563EB',
      site_loyalty_page_url: null,
      site_url : null
    };
  }


  getSetting<K extends keyof AppSettings>(key: K): AppSettings[K] {
    return this.settings?.[key] ?? this.getFallbackSettings()[key];
  }


  getAllSettings(): AppSettings {
    return this.settings || this.getFallbackSettings();
  }

 
  isLoaded(): boolean {
    return this.settings !== null;
  }

 
  subscribe(callback: (settings: AppSettings | null) => void): () => void {
    this.subscribers.push(callback);
    
    
    if (this.settings) {
      callback(this.settings);
    }
    
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }


  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.settings));
  }


  updateSettings(newSettings: AppSettings): void {
    this.settings = newSettings;
    this.notifySubscribers();
  }

 
  clearSettings(): void {
    this.settings = null;
    this.notifySubscribers();
  }
}


export const settingsService = new SettingsService();